import React, { useState, useRef, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import styles from './OpenReportButton.module.scss';
import Report from './Report';
import { ProtocolPanelContext } from './ProtocolPanelContext';

import { useTranslation } from 'react-i18next';

export default function OpenReportButton({ locationType, clientReportUid }) {
  let { consultationUid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportOpened, setReportOpened] = useState(false);

  const [savedTab, setSavedTab] = useState(null);
  const [savedScroll, setSavedScroll] = useState(0);
  const [consultationRecordUid, setConsultationRecordUid] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const reportRef = useRef();

  const reportUrl = (() => {
    switch (locationType) {
      case 'accepted':
        return `/consultation/expert/accepted/${consultationUid}/report`;
      case 'completed':
        return `/consultation/expert/completed/${consultationUid}/report`;
      case 'client':
        return `/consultation/user/report/${clientReportUid}`;
      default:
        return ``;
    }
  })();
  const completeReportRedirectUrl = locationType === 'accepted' ? '/consultations/expert/accepted/list' : '';
  const btnDefaultText = locationType === 'client' ? t('consultation.button.view') : t('consultation.button.open');
  const btnStyle = (locationType === 'client' && !visible) ? styles.regular : styles.floating;

  const openReportClick = () => {
    if (!visible) {
      setReportLoading(true);
      setVisible(true);
    }
    else {
      const info = reportRef.current?.getScrollInfo?.();
      if (info) {        
        setSavedScroll(info.scrollTop);
        setSavedTab(info.tab);
      }

      setVisible(false);
      setReportOpened(false);
    }
  };

  const reportLoadComplete = (reportData) => {
    setReportLoading(false);
    setReportOpened(true);
    if (reportData) {
      setConsultationRecordUid(reportData.consultationRecordUid);
      setIsReadOnly(reportData.isReadOnly);
    }
  };

  const closePanel = () => {
    const info = reportRef.current?.getScrollInfo?.();
    if (info) {
      setSavedScroll(info.scrollTop);
      setSavedTab(info.tab);
    }
    setVisible(false);
    setReportOpened(false);
  };

  const navigateToEcg = () => {
    closePanel();
    if (consultationRecordUid && consultationUid) {
      // Navigate to the ECG page for the current consultation record
      const basePath = locationType === 'accepted'
        ? `/consultations/expert/accepted/${consultationUid}/current_record/${consultationRecordUid}`
        : `/consultations/expert/completed/${consultationUid}/current_record/${consultationRecordUid}`;
      navigate(basePath);
    }
  };

  const contextValue = useMemo(() => ({
    closePanel,
    navigateToEcg,
    consultationRecordUid,
    consultationUid,
    locationType,
    isReadOnly
  }), [consultationRecordUid, consultationUid, locationType, isReadOnly]);

  return (
    <>
      <Button variant="primary" className={btnStyle} onClick={openReportClick}>
        {reportLoading && <Spinner animation="border" variant="warning" className={"me-2"} />} {reportOpened ? t('consultation.button.close') : btnDefaultText}
      </Button>
      {visible &&
        <ProtocolPanelContext.Provider value={contextValue}>
          <Report
            ref={reportRef}
            reportUrl={reportUrl}
            completeReportRedirectUrl={completeReportRedirectUrl}
            reportLoadCompleteCallback={reportLoadComplete}
            savedTab={savedTab}
            savedScroll={savedScroll}
          />
        </ProtocolPanelContext.Provider>
      }
    </>
  );
}

